/**
 * Secure password generation utility for trial user provisioning
 */

export interface GeneratedPassword {
  password: string;
  strength: 'strong';
  requirements: string[];
}

/**
 * Generates a secure 12-character password with mixed case, numbers, and symbols
 */
export function generateSecurePassword(length: number = 12): GeneratedPassword {
  // Character sets for password generation
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*';
  
  // Ensure minimum requirements are met
  const requirements = [
    'At least 1 lowercase letter',
    'At least 1 uppercase letter', 
    'At least 1 number',
    'At least 1 special character',
    `${length} characters total`
  ];
  
  // Start with one character from each required set
  let password = '';
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  
  // Fill remaining length with random characters from all sets
  const allChars = lowercase + uppercase + numbers + symbols;
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password to avoid predictable patterns
  password = password.split('').sort(() => Math.random() - 0.5).join('');
  
  return {
    password,
    strength: 'strong',
    requirements
  };
}

/**
 * Copies text to clipboard with fallback for older browsers
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for non-HTTPS or older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}

/**
 * Validates password strength
 */
export function validatePasswordStrength(password: string): {
  isStrong: boolean;
  score: number;
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;
  
  if (password.length >= 12) score += 25;
  else feedback.push('Password should be at least 12 characters');
  
  if (/[a-z]/.test(password)) score += 25;
  else feedback.push('Add lowercase letters');
  
  if (/[A-Z]/.test(password)) score += 25;
  else feedback.push('Add uppercase letters');
  
  if (/[0-9]/.test(password)) score += 15;
  else feedback.push('Add numbers');
  
  if (/[^a-zA-Z0-9]/.test(password)) score += 10;
  else feedback.push('Add special characters');
  
  return {
    isStrong: score >= 90,
    score,
    feedback
  };
}